import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/** Company-wide business context — ``GET/PATCH /business-context``. */
export type TenantBusinessContext = {
  industry: string | null;
  company_overview: string | null;
  company_history: string | null;
};

export const businessContextEmpty: TenantBusinessContext = {
  industry: null,
  company_overview: null,
  company_history: null,
};

export type BusinessContextState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  patchStatus: "idle" | "loading" | "succeeded" | "failed";
  patchError: string | null;
  businessContext: TenantBusinessContext;
};

export const businessContextInitialState: BusinessContextState = {
  status: STATUS.IDLE,
  error: null,
  patchStatus: STATUS.IDLE,
  patchError: null,
  businessContext: businessContextEmpty,
};

function isTenantBusinessContext(value: unknown): value is TenantBusinessContext {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (typeof row.industry === "string" || row.industry === null) &&
    (typeof row.company_overview === "string" || row.company_overview === null) &&
    (typeof row.company_history === "string" || row.company_history === null)
  );
}

export const fetchBusinessContext = createAsyncThunk<
  TenantBusinessContext,
  void,
  { rejectValue: string }
>("businessContext/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/business-context", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isTenantBusinessContext(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const patchBusinessContext = createAsyncThunk<
  TenantBusinessContext,
  TenantBusinessContext,
  { rejectValue: string }
>("businessContext/patch", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/business-context", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isTenantBusinessContext(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const businessContextSlice = createSlice({
  name: "businessContext",
  initialState: businessContextInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchBusinessContext.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchBusinessContext.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.businessContext = action.payload;
      })
      .addCase(fetchBusinessContext.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(patchBusinessContext.pending, (state) => {
        state.patchStatus = STATUS.LOADING;
        state.patchError = null;
      })
      .addCase(patchBusinessContext.fulfilled, (state, action) => {
        state.patchStatus = STATUS.SUCCEEDED;
        state.businessContext = action.payload;
        state.patchError = null;
      })
      .addCase(patchBusinessContext.rejected, (state, action) => {
        state.patchStatus = STATUS.FAILED;
        state.patchError = action.payload ?? "Request failed";
      });
  },
});

export const businessContextReducer = businessContextSlice.reducer;
